import { toast } from "@/components/common/Toast"
import { Button } from "@/components/ui/button"
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useRegisterAgentContext } from "@/features/agent-chat/context/AgentPageContext"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useQueryClient } from "@tanstack/react-query"
import { ArrowRight } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import {
  downloadImportFile,
  useAnalyzedFiles,
  useCommitImportFile,
  useConfirmMapping,
  useDeleteImportFile,
  useFileRowsPages,
  type CommitResult,
  type StagedRow,
  type SupplierNumberTwinsMode,
} from "../../api/importsApi"
import { type EntityGroup } from "../../api/ingestionApi"
import { useTemplateSettings } from "../../api/templateSettingsApi"
import type { DataQualityWarning } from "../../api/types/imports"
import { FooterActionBar } from "../shared/FooterActionBar"
import { ModuleHeader } from "../shared/ModuleHeader"
import { pollCommitStatus } from "./commitPolling"
import { DataQualityWarnings } from "./DataQualityWarnings"
import { MappingColumnList } from "./MappingColumnList"
import { MappingPreviewTable, type PreviewRow } from "./MappingPreviewTable"
import {
  analyzedFileColumns,
  analyzedFilesToGroups,
  analyzedFilesToSkippedComplianceColumns,
  analyzedFilesToSupplierNumberTwins,
  buildOverridePayload,
  countAssignments,
  formatAssignmentLabel,
  groupColumnsBySheet,
  scopedColumnKey,
  sumEntityCounts,
  visibleSheetNames,
  type MappingColumn,
} from "./mappingUtils"
import {
  MasterDataCommitSummary,
  type CommitSummaryData,
} from "./MasterDataCommitSummary"
import {
  IMPORT_REVIEW_STEP,
  MASTER_DATA_MAPPING_ROUTE,
} from "./masterDataImportAgentTargets"
import { MasterDataMappingModuleSkeleton } from "./MasterDataMappingModuleSkeleton"
import {
  MasterDataMappingStepCards,
  type MappingStepCard,
} from "./MasterDataMappingStepCards"
import { MasterDataReviewStep } from "./MasterDataReviewStep"

// ─── Constants ────────────────────────────────────────────────────────────────

const REVIEW_STEP_ID = "__review__"

const BTN_PRIMARY = "bg-yellow1 text-black hover:bg-yellow2"

const ROWS_PER_PAGE = 10

// ─── Types ────────────────────────────────────────────────────────────────────

type DerivedFileStep = {
  id: string
  fileName: string
  columns: MappingColumn[]
  /** Visible worksheet names, in order. Empty for CSVs / single-sheet files. */
  sheetNames: string[]
  /** T2: gates the BOM-root-promotion exemption in the product identity guard. */
  hierarchyMode: string | null
  supportsBomRootPromotion?: boolean
  /** The backend sent a download link. Not the link itself: that one expires
   *  while the user maps, so the download fetches a fresh one. */
  hasDownload: boolean
}

// ─── File mapping step ────────────────────────────────────────────────────────

type FileMappingStepProps = {
  fileStep: DerivedFileStep
  warnings: DataQualityWarning[]
  activeSheetName: string | null
  activeColumnRef: string | null
  hasLocalChanges: boolean
  onActiveColumnChange: (sheetName: string | null, columnRef: string) => void
  /** All three keyed by `scopedColumnKey`, not the bare column name (T1). */
  onAssignmentChange: (columnKey: string, assignment: string | null) => void
  onResetAssignment: (columnKey: string, assignment: string | null) => void
  resolveAssignment: (
    columnKey: string,
    aiAssigned: string | null,
  ) => string | null
  onDiscard: () => void
  onNext: () => void
  nextLabel: string
}

function FileMappingStep({
  fileStep,
  warnings,
  activeSheetName,
  activeColumnRef,
  hasLocalChanges,
  onActiveColumnChange,
  onAssignmentChange,
  onResetAssignment,
  resolveAssignment,
  onDiscard,
  onNext,
  nextLabel,
}: FileMappingStepProps) {
  const { translate } = useTranslation()
  const { data: templateSettings } = useTemplateSettings()

  // A skipped column is decided, so it never blocks Next, but it is not
  // counted as mapped either.
  const {
    mapped: mappedCount,
    skipped: skippedCount,
    unmapped: unmappedCount,
  } = countAssignments(
    fileStep.columns.map((column) =>
      resolveAssignment(
        scopedColumnKey(column.sheetName, column.columnName),
        column.aiAssigned,
      ),
    ),
  )
  const allMapped = unmappedCount === 0
  // Skipping every column leaves nothing to import: the backend refuses such a
  // file at commit (ALL_COLUMNS_SKIPPED), and that failure also stops the rest
  // of the batch. So Next stays disabled until one column is mapped.
  const allSkipped =
    skippedCount > 0 && skippedCount === fileStep.columns.length

  // Review finding (TUR-1539 spec §3.6): GROUP_COLUMN_AMBIGUOUS is a
  // 'blocking' mapping-validation warning, but nothing enforced it — confirm
  // must stay disabled while the CURRENT effective assignment (AI mapping +
  // this file's overrides) still has two or more columns on
  // articles:product_group, counted PER SHEET (T1), mirroring the backend's
  // confirm_mapping check (mc_imports_service.py). A column with no sheet
  // (CSV / single-sheet, or a column the analyzer never tagged) applies to
  // every sheet, so it counts toward each sheet's total. Deduped by header
  // within a sheet's bucket: the same header appearing twice for one sheet is
  // one column the user can act on with a single override, not two competing
  // ones. Same dedup basis as the backend, so a state the backend confirms
  // can never show as blocked here (or the ambiguity would be unclearable).
  const competingGroupColumns = useMemo(() => {
    const bySheet = new Map<string | null, MappingColumn[]>()
    for (const column of fileStep.columns) {
      if (
        resolveAssignment(
          scopedColumnKey(column.sheetName, column.columnName),
          column.aiAssigned,
        ) !== "articles:product_group"
      ) {
        continue
      }
      const list = bySheet.get(column.sheetName) ?? []
      list.push(column)
      bySheet.set(column.sheetName, list)
    }

    const dedupeByHeader = (cols: MappingColumn[]) => {
      const byName = new Map<string, MappingColumn>()
      for (const column of cols) byName.set(column.columnName, column)
      return [...byName.values()]
    }

    const sheetless = bySheet.get(null) ?? []
    const namedSheets = [...bySheet.keys()].filter(
      (sheet): sheet is string => sheet !== null,
    )

    if (namedSheets.length === 0) {
      // No sheet carries a group column at all (legacy stored mapping, or a
      // CSV / single-sheet import): the pre-T1 global, header-deduped check.
      return dedupeByHeader(sheetless)
    }
    for (const sheet of namedSheets) {
      const combined = dedupeByHeader([
        ...(bySheet.get(sheet) ?? []),
        ...sheetless,
      ])
      if (combined.length > 1) return combined
    }
    return []
  }, [fileStep.columns, resolveAssignment])
  const hasCompetingGroupColumns = competingGroupColumns.length > 1
  const productGroupLabel =
    templateSettings?.productGroupLabel ||
    translate("MC_MD_FIELD_PRODUCT_GROUP")

  // T2: mirrors the backend's confirm-time PRODUCT_NUMBER_UNMAPPED check
  // (mc_imports_service.py) — a sheet whose CURRENT effective assignment (AI
  // mapping + this file's overrides) maps any products.* attribute without
  // products.product_number, and isn't rescued by BOM-root promotion
  // (hierarchy mode + the sheet's own articles.article_number), must keep
  // Confirm disabled. Same per-sheet, sheetless-applies-everywhere grouping
  // basis as competingGroupColumns above, so a state the backend confirms
  // can never show as blocked here.
  const productsMissingNumber = useMemo(() => {
    const bySheet = new Map<string | null, MappingColumn[]>()
    for (const column of fileStep.columns) {
      const list = bySheet.get(column.sheetName) ?? []
      list.push(column)
      bySheet.set(column.sheetName, list)
    }

    const assignmentOf = (column: MappingColumn) =>
      resolveAssignment(
        scopedColumnKey(column.sheetName, column.columnName),
        column.aiAssigned,
      )
    // The backend's own answer, never re-derived here: it owns the rule confirm
    // enforces, and a second copy in TypeScript is what drifted (this exempted
    // `indented`, which promotes no row, so Confirm was enabled and 409'd).
    const supportsBomRootPromotion = fileStep.supportsBomRootPromotion === true

    const check = (
      sheet: string | null,
      combined: MappingColumn[],
    ): { sheet: string | null; columns: MappingColumn[] } | null => {
      const productColumns = combined.filter((column) => {
        const assignment = assignmentOf(column)
        return assignment === "products" || assignment?.startsWith("products:")
      })
      if (!productColumns.length) return null
      const hasProductNumber = productColumns.some(
        (column) => assignmentOf(column) === "products:product_number",
      )
      if (hasProductNumber) return null
      const hasArticleNumber = combined.some(
        (column) => assignmentOf(column) === "articles:article_number",
      )
      if (supportsBomRootPromotion && hasArticleNumber) return null
      return { sheet, columns: productColumns }
    }

    const sheetless = bySheet.get(null) ?? []
    const namedSheets = [...bySheet.keys()].filter(
      (sheet): sheet is string => sheet !== null,
    )

    if (namedSheets.length === 0) {
      return check(null, sheetless)
    }
    for (const sheet of namedSheets) {
      const hit = check(sheet, [...(bySheet.get(sheet) ?? []), ...sheetless])
      if (hit) return hit
    }
    return null
  }, [fileStep.columns, fileStep.supportsBomRootPromotion, resolveAssignment])
  const hasProductsMissingNumber = productsMissingNumber !== null
  const productsMissingNumberMessage = productsMissingNumber
    ? translate("MC_MD_MAP_PRODUCT_NUMBER_UNMAPPED", {
        sheet: productsMissingNumber.sheet ?? fileStep.fileName,
      })
    : undefined

  const isMultiSheet = fileStep.sheetNames.length > 1
  const sheetGroups = useMemo(
    () => groupColumnsBySheet(fileStep.columns),
    [fileStep.columns],
  )
  const sheetColumns = useMemo(
    () =>
      isMultiSheet
        ? (sheetGroups.find((g) => g.sheetName === activeSheetName)?.columns ??
          [])
        : fileStep.columns,
    [isMultiSheet, sheetGroups, activeSheetName, fileStep.columns],
  )

  const {
    data,
    isLoading: isInitialLoading,
    isFetchingNextPage: isLoadingMore,
    hasNextPage,
    fetchNextPage,
    isError: isRowsError,
  } = useFileRowsPages({
    fileId: fileStep.id,
    limit: ROWS_PER_PAGE,
    sheetName: isMultiSheet ? (activeSheetName ?? undefined) : undefined,
  })

  const allRows = useMemo<StagedRow[]>(
    () => data?.pages.flatMap((p) => p.rows) ?? [],
    [data],
  )
  const hasMore = !!hasNextPage
  const totalRows = data?.pages[0]?.totalRows ?? 0

  useEffect(() => {
    if (isRowsError) toast.error(translate("MC_MD_MAP_ROWS_ERROR"))
  }, [isRowsError, translate])

  const previewRows = useMemo<PreviewRow[]>(
    () =>
      allRows.map((row) => {
        const record: PreviewRow = {}
        for (const column of sheetColumns) {
          record[column.columnRef] = row.values[column.columnIndex] ?? "—"
        }
        return record
      }),
    [allRows, sheetColumns],
  )

  const handleSheetChange = useCallback(
    (sheetName: string) => {
      const firstColumnRef =
        sheetGroups.find((g) => g.sheetName === sheetName)?.columns[0]
          ?.columnRef ?? ""
      onActiveColumnChange(sheetName, firstColumnRef)
    },
    [sheetGroups, onActiveColumnChange],
  )

  // A set of file ids, not a boolean or a single id: this step stays mounted
  // while the user moves between files, and downloads can overlap (start A,
  // move to B and start it, come back to A while both run).
  const [downloadingFileIds, setDownloadingFileIds] = useState<
    ReadonlySet<string>
  >(() => new Set())
  const handleDownload = useCallback(async () => {
    const fileId = fileStep.id
    setDownloadingFileIds((current) => new Set(current).add(fileId))
    try {
      await downloadImportFile(fileId)
    } catch {
      toast.error(translate("MC_MD_MAP_DOWNLOAD_ERROR"))
    } finally {
      setDownloadingFileIds((current) => {
        const next = new Set(current)
        next.delete(fileId)
        return next
      })
    }
  }, [fileStep.id, translate])

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {warnings.length > 0 && (
        <div className="px-6 pt-3">
          <DataQualityWarnings warnings={warnings} />
        </div>
      )}
      {hasCompetingGroupColumns && (
        <div className="px-6 pt-3">
          <div className="rounded-lg border border-red1/20 bg-red1/4 px-4 py-2.5 text-[12.5px] leading-[1.55] text-white">
            {translate("MC_MD_MAP_GROUP_COLUMNS_COMPETING", {
              label: productGroupLabel,
              columns: competingGroupColumns
                .map((column) => column.columnName)
                .join(", "),
            })}
          </div>
        </div>
      )}
      {hasProductsMissingNumber && (
        <div className="px-6 pt-3">
          <div className="rounded-lg border border-red1/20 bg-red1/4 px-4 py-2.5 text-[12.5px] leading-[1.55] text-white">
            {productsMissingNumberMessage}
          </div>
        </div>
      )}
      {allSkipped && (
        <div className="px-6 pt-3">
          <div className="rounded-lg border border-red1/20 bg-red1/4 px-4 py-2.5 text-[12.5px] leading-[1.55] text-white">
            {translate("MC_MD_MAP_ALL_COLUMNS_SKIPPED")}
          </div>
        </div>
      )}
      <div className="min-h-0 flex-1 px-6 py-4 pb-0">
        <div className="h-full overflow-hidden rounded-lg border">
          <ResizablePanelGroup orientation="horizontal">
            <ResizablePanel defaultSize={42} minSize={28}>
              <MappingColumnList
                columns={fileStep.columns}
                activeSheetName={activeSheetName}
                activeColumnRef={activeColumnRef}
                onActiveColumnChange={onActiveColumnChange}
                resolveAssignment={resolveAssignment}
                onAssignmentChange={onAssignmentChange}
                onResetAssignment={onResetAssignment}
              />
            </ResizablePanel>
            <ResizableHandle withHandle />
            <ResizablePanel defaultSize={58} minSize={30}>
              <MappingPreviewTable
                title={fileStep.fileName}
                columns={sheetColumns}
                rows={previewRows}
                totalRows={totalRows}
                isLoading={isInitialLoading}
                hasMore={hasMore}
                isLoadingMore={isLoadingMore}
                onLoadMore={fetchNextPage}
                activeColumnRef={activeColumnRef}
                onActiveColumnChange={(columnRef) =>
                  onActiveColumnChange(activeSheetName, columnRef)
                }
                resolveAssignment={resolveAssignment}
                onAssignmentChange={onAssignmentChange}
                formatTargetLabel={(assignment) =>
                  formatAssignmentLabel(assignment, translate)
                }
                placeholderLabel={translate("MC_MD_MAP_PICK_ENTITY")}
                sheetNames={fileStep.sheetNames}
                activeSheetName={activeSheetName}
                onSheetChange={handleSheetChange}
                onDownload={fileStep.hasDownload ? handleDownload : undefined}
                isDownloading={downloadingFileIds.has(fileStep.id)}
              />
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>
      </div>

      <FooterActionBar
        left={
          <p className="text-sm">
            {unmappedCount > 0 && (
              <>
                <span className="font-medium text-warning">
                  {translate("MC_MD_MAP_NEEDS_TARGET", {
                    count: unmappedCount,
                  })}
                </span>{" "}
                ·{" "}
              </>
            )}
            {translate("MC_MD_MAP_MAPPED", {
              assigned: mappedCount,
              total: fileStep.columns.length - skippedCount,
            })}
            {skippedCount > 0 && (
              <>
                {" "}
                ·{" "}
                {translate("MC_MD_MAP_SKIPPED_COUNT", { count: skippedCount })}
              </>
            )}
          </p>
        }
      >
        {/* Discard resets local overrides for this file; disabled when none exist */}
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                onClick={onDiscard}
                disabled={!hasLocalChanges}
              >
                {translate("MC_MD_MAP_DISCARD")}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[220px]">
              {translate("MC_MD_MAP_DISCARD_TOOLTIP")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <Button
          // The control the agent hands over to for a file still needing its
          // mapping confirmed — it both accepts this file's mapping and walks to
          // the next step, which on the last file is Review & Commit. Built per
          // action rather than catalogued, see `masterDataImportAgentTargets`.
          data-agent-target="import.confirm-mapping-button"
          className={BTN_PRIMARY}
          onClick={onNext}
          disabled={
            !allMapped ||
            allSkipped ||
            hasCompetingGroupColumns ||
            hasProductsMissingNumber
          }
          title={
            hasCompetingGroupColumns
              ? translate("MC_MD_MAP_GROUP_COLUMNS_COMPETING", {
                  label: productGroupLabel,
                  columns: competingGroupColumns
                    .map((column) => column.columnName)
                    .join(", "),
                })
              : hasProductsMissingNumber
                ? productsMissingNumberMessage
                : allSkipped
                  ? translate("MC_MD_MAP_ALL_COLUMNS_SKIPPED")
                  : allMapped
                    ? undefined
                    : translate("MC_MD_MAP_NEEDS_TARGET", {
                        count: unmappedCount,
                      })
          }
        >
          {nextLabel}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </FooterActionBar>
    </div>
  )
}

// ─── Root module ──────────────────────────────────────────────────────────────

/** The incoming supplier numbers a confirm found twins for. Confirm's response skips the
 *  camelCase pass (its request needs `skipCaseConversion`), so the list is snake_case. */
function confirmedTwinNumbers(confirmed: unknown): string[] {
  const twins = (
    confirmed as { supplier_number_twins?: { incoming: string }[] } | undefined
  )?.supplier_number_twins
  return (twins ?? []).map((t) => t.incoming)
}

export function MasterDataMappingModule() {
  const navigate = useNavigate()
  const { translate } = useTranslation()
  const [searchParams] = useSearchParams()
  const fileIds = searchParams.getAll("file_ids")

  // The mapping screen owns its data: it fetches the analyzed files directly
  // and derives everything (column rows, entity groups) from that response.
  useEffect(() => {
    if (fileIds.length === 0) {
      navigate("/material-compliance/master-data/import", { replace: true })
    }
  }, [fileIds.length, navigate])

  const [isCommitting, setIsCommitting] = useState(false)
  const queryClient = useQueryClient()
  // Freezes the analyzed-files query (keeps its last-known data, no
  // background refetch) for the commit phase of handleCommit only — kept
  // separate from isCommitting because the earlier confirm phase needs the
  // query enabled: useConfirmMapping's onSuccess invalidates it and
  // handleCommit awaits that, so a query already disabled at that point makes
  // the await a no-op (see the ordering comment in handleCommit).
  //
  // The QueryClient is created with refetchOnWindowFocus app-wide and this
  // hook has no staleTime, so a tab switch mid-commit would otherwise
  // refetch — and the backend excludes COMMITTING rows from list_analyzed,
  // which would empty the review step while the commit is still running. On
  // a confirmed FAILED status this unfreezes explicitly (FAILED rows ARE
  // part of list_analyzed, so the file comes back with a retry path); on a
  // thrown poll/commit error the backend's real status is unknown, so this
  // deliberately stays frozen — an automatic refetch could show a false
  // empty state for a file that is, in fact, still COMMITTING. isCommitting
  // itself still flips back to false either way, so Commit stays reachable.
  const [analyzedQueryFrozen, setAnalyzedQueryFrozen] = useState(false)
  const { data: analyzedFiles = [], isLoading } = useAnalyzedFiles({
    fileIds,
    enabled: !analyzedQueryFrozen,
  })
  const { mutateAsync: confirmMapping } = useConfirmMapping()
  const { mutateAsync: commitFile } = useCommitImportFile()
  const { mutateAsync: deleteFile } = useDeleteImportFile()
  // Org's configured label for articles.product_group, used in the review
  // step's reimport option copy.
  const { data: orgTemplateSettings } = useTemplateSettings()
  const productGroupLabel =
    orgTemplateSettings?.productGroupLabel ||
    translate("MC_MD_FIELD_PRODUCT_GROUP")

  const [selectedGroup, setSelectedGroup] = useState<string>("")
  // fileId -> scopedColumnKey -> assignment (T1: keyed by scopedColumnKey, not
  // the bare column name. A header repeated across sheets, e.g. a
  // distributor's "Teilenr" naming both the article and the product number,
  // would otherwise collide and move the wrong sheet's row on override).
  const [columnOverrides, setColumnOverrides] = useState<
    Record<string, Record<string, string | null>>
  >({})
  // Q19 import option — offered on the review step while conformity v2 is on.
  const [soldAsIs, setSoldAsIs] = useState(false)
  // TUR-1539 §3.6: keep-empty (default) vs overwrite an existing product_group
  // when this import's row for an article carries a blank value.
  const [productGroupReimportMode, setProductGroupReimportMode] = useState<
    "keep_empty" | "overwrite"
  >("keep_empty")
  // Suffix twins ("0011335 000" vs an existing "0011335"): use the existing supplier
  // unless the user switches it off on the review step.
  const [supplierNumberTwinsMode, setSupplierNumberTwinsMode] =
    useState<SupplierNumberTwinsMode>("link")
  const [commitSummary, setCommitSummary] = useState<CommitSummaryData | null>(
    null,
  )
  const [committedResults, setCommittedResults] = useState<
    Record<string, CommitResult>
  >({})
  const [deletedFileIds, setDeletedFileIds] = useState<string[]>([])
  // Tracks which file is currently being deleted (shows spinner on that card).
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null)

  const groups = useMemo<EntityGroup[]>(
    () => analyzedFilesToGroups(analyzedFiles),
    [analyzedFiles],
  )
  const skippedComplianceColumns = useMemo(
    () => analyzedFilesToSkippedComplianceColumns(analyzedFiles),
    [analyzedFiles],
  )
  const supplierNumberTwins = useMemo(
    () => analyzedFilesToSupplierNumberTwins(analyzedFiles),
    [analyzedFiles],
  )

  const fileSteps = useMemo<DerivedFileStep[]>(
    () =>
      analyzedFiles
        .filter((file) => !deletedFileIds.includes(file.fileId))
        .map((file) => ({
          id: file.fileId,
          fileName: file.filename,
          columns: analyzedFileColumns(file),
          sheetNames: visibleSheetNames(file),
          hierarchyMode: file.hierarchyMode,
          supportsBomRootPromotion: file.supportsBomRootPromotion,
          hasDownload: Boolean(file.downloadUrl),
        })),
    [analyzedFiles, deletedFileIds],
  )

  // "Start another import" clears local review state and returns to upload.
  const reset = useCallback(() => {
    setSelectedGroup("")
    setColumnOverrides({})
    setDeletedFileIds([])
    setCompletedFileIds([])
    setCommittedResults({})
    navigate("/material-compliance/master-data/import")
  }, [navigate])

  // Delete a file via the API, then remove it from the local flow.
  // If it was the last file, redirect to the upload screen.
  const handleDeleteFile = useCallback(
    async (fileId: string) => {
      if (deletingFileId) return
      setDeletingFileId(fileId)
      try {
        await deleteFile(fileId)
      } catch {
        toast.error(translate("MC_MD_MAP_DELETE_FILE_ERROR"))
        setDeletingFileId(null)
        return
      }
      setDeletingFileId(null)
      // Computed before setState — the updater passed to setState must stay
      // pure (no side effects); React can invoke it more than once (e.g.
      // StrictMode's double-invoke check), which would fire navigate() twice.
      const nextDeletedFileIds = [...deletedFileIds, fileId]
      setDeletedFileIds(nextDeletedFileIds)
      // A deleted file must not keep haunting the commit summary (TUR-1522):
      // drop any prior committed/failed result for it.
      setCommittedResults((prev) => {
        const next = { ...prev }
        delete next[fileId]
        return next
      })
      // If that was the last file, redirect immediately.
      const remaining = analyzedFiles.filter(
        (f) => !nextDeletedFileIds.includes(f.fileId),
      )
      if (remaining.length === 0) {
        navigate("/material-compliance/master-data/import", {
          replace: true,
        })
      }
      setColumnOverrides((prev) => {
        const next = { ...prev }
        delete next[fileId]
        return next
      })
      setActiveStepId((current) => {
        if (current !== fileId) return current
        const remaining = fileSteps.filter((s) => s.id !== fileId)
        return remaining[0]?.id ?? REVIEW_STEP_ID
      })
    },
    [
      deletingFileId,
      deleteFile,
      deletedFileIds,
      analyzedFiles,
      fileSteps,
      navigate,
      translate,
    ],
  )

  // Discard local overrides for one file (revert to AI suggestions).
  const handleDiscardFileOverrides = useCallback((fileId: string) => {
    setColumnOverrides((prev) => {
      const next = { ...prev }
      delete next[fileId]
      return next
    })
  }, [])

  // Confirm any per-file column overrides, then commit every analyzed file.
  // On success, build the committed summary from the real commit results.
  // Files already committed by an earlier, partially-failed attempt are
  // skipped — commitFile is not idempotent, so a retry must not re-send it.
  const handleCommit = useCallback(async () => {
    if (isCommitting) return
    setIsCommitting(true)
    try {
      const activeFiles = analyzedFiles.filter(
        (f) => !deletedFileIds.includes(f.fileId),
      )
      // A file already in `committedResults` with row errors did NOT actually
      // commit (it was rolled back) — it must stay in `remaining` so the user
      // can fix the mapping/file and re-commit. Only a clean, error-free entry
      // means "already committed, don't re-send".
      const remaining = activeFiles.filter((f) => {
        const prior = committedResults[f.fileId]
        return !prior || prior.errors.length > 0
      })
      const accumulated = Object.fromEntries(
        Object.entries(committedResults).filter(
          ([fileId]) => !deletedFileIds.includes(fileId),
        ),
      )

      // Phase 1 — confirm every file's overrides first, analyzed-files query
      // still enabled. useConfirmMapping's onSuccess invalidates that query
      // and this await only actually waits for a refetch while an observer
      // for it is enabled (TanStack Query skips a disabled query on
      // invalidate) — freezing the query before this phase made the await
      // resolve against stale, pre-confirm cache data instead.
      const shownTwins = new Set(supplierNumberTwins.map((t) => t.incoming))
      let unseenTwins = false
      for (const file of remaining) {
        const fileOverrides = columnOverrides[file.fileId]
        // soldAsIs is sent for every file in the batch: the commit runner only
        // acts on article rows WITHOUT a product, so it is a no-op for pure
        // product/supplier files (review nit).
        //
        // Confirm always runs and always sends an explicit boolean (never
        // omitted): confirmMapping persists mapping['sold_as_is'] until told
        // otherwise, so an earlier attempt that stored `true` would otherwise
        // survive a retry where the user hid the switch (mapped a product key —
        // the reset effect below then clears `soldAsIs`) or simply unchecked it.
        const confirmed = await confirmMapping({
          fileId: file.fileId,
          payload: {
            // Resolves each override's scoped key back to its sheetName so
            // buildOverridePayload can send `sourceSheet` alongside it (T1).
            ...buildOverridePayload(
              fileOverrides ?? {},
              analyzedFileColumns(file),
            ),
            soldAsIs,
            productGroupReimportMode,
            supplierNumberTwinsMode,
          },
        })
        if (confirmedTwinNumbers(confirmed).some((n) => !shownTwins.has(n)))
          unseenTwins = true
      }
      // A column mapped on the mapping step can surface supplier-number twins the
      // review step never showed. Stop before committing: the refetched analysis
      // now lists them, so the user sees the link/create choice before it applies.
      if (unseenTwins) {
        toast.warning(translate("MC_MD_REVIEW_SUPPLIER_TWINS_NEW"))
        return
      }

      // Phase 2 — schedule and poll each commit. Freeze the analyzed-files
      // query for this phase only: the backend excludes a COMMITTING row
      // from list_analyzed, so a background refetch (e.g. window refocus)
      // would otherwise empty the review step while a commit is in flight.
      setAnalyzedQueryFrozen(true)
      for (const file of remaining) {
        // Commit is async: schedule it, then poll until it lands. This is what
        // lets huge files (tens of thousands of rows) commit without hitting the
        // request timeout.
        await commitFile(file.fileId)
        const status = await pollCommitStatus(file.fileId)
        if (status.status === "failed") {
          // A genuinely terminal, known outcome — FAILED rows ARE part of
          // list_analyzed, so it is safe to unfreeze here, before the error
          // below surfaces and the file (with a retry path) comes back.
          setAnalyzedQueryFrozen(false)
          const rowErrors = status.errors ?? []
          if (rowErrors.length === 0)
            throw new Error(status.error ?? "commit failed")
          // Structured row errors (TUR-1522): the commit was rolled back but the
          // failure is user-fixable, so surface it in the summary instead of the
          // generic "commit failed" toast — and stop the walk here rather than
          // trying the remaining files.
          accumulated[file.fileId] = {
            fileId: file.fileId,
            recordsCreated: {},
            recordsUpdated: {},
            aliasesSaved: 0,
            errors: rowErrors,
            warnings: status.warnings ?? [],
          }
          setCommittedResults({ ...accumulated })
          // Some failure codes are whole-file (ALL_COLUMNS_SKIPPED,
          // NO_DATA_ROWS, NO_INPUT, DB_ERROR — row: null), not per-row: the
          // row-count toast only fits when at least one error names a row.
          toast.error(
            rowErrors.some((e) => e.row !== null)
              ? translate("MC_MD_MAP_COMMIT_ROW_ERRORS", {
                  n: rowErrors.length,
                })
              : translate("MC_MD_MAP_COMMIT_ERROR"),
          )
          break
        }
        // The commit has landed, so master data moved: this is the moment
        // the data-gaps counts (TUR-1539) go stale, not when the commit was
        // scheduled (useCommitImportFile), because the nav pill keeps that
        // query active and a refetch then would cache pre-commit counts.
        queryClient.invalidateQueries({
          queryKey: ["mc", "master-data", "data-gaps"],
        })
        accumulated[file.fileId] = {
          fileId: file.fileId,
          recordsCreated: status.rowCounts ?? {},
          // Older commits persisted no update counts; the summary hides aliases when 0.
          recordsUpdated: status.updatedCounts ?? {},
          aliasesSaved: 0,
          errors: [],
          warnings: status.warnings ?? [],
        }
        setCommittedResults({ ...accumulated })
      }
      setAnalyzedQueryFrozen(false)

      const results = Object.values(accumulated)
      setCommitSummary({
        createdCounts: sumEntityCounts(results.map((r) => r.recordsCreated)),
        updatedCounts: sumEntityCounts(results.map((r) => r.recordsUpdated)),
        aliasesSaved: results.reduce(
          (sum, r) => sum + (r.aliasesSaved ?? 0),
          0,
        ),
        warnings: results.flatMap((r) => r.warnings ?? []),
        errors: results.flatMap((r) => r.errors ?? []),
        results,
      })
    } catch (error) {
      // Error responses skip the camelCase interceptor, so the wire key is read raw.
      const code = (
        error as { response?: { data?: { detail?: { code?: string } } } }
      )?.response?.data?.detail?.code
      if (code === "BOM_NO_PARENT") {
        toast.error(translate("MC_MD_MAP_BOM_NO_PARENT"))
        return
      }
      if (code === "NOTHING_IMPORTED") {
        toast.error(translate("MC_MD_IMPORT_ERR_NOTHING_IMPORTED"))
        return
      }
      // Failure surfaces four ways: the FAILED poll state (thrown above,
      // which already unfroze the query), a thrown pollCommitStatus/commitFile
      // error (transport failure or poll timeout — the backend row's real
      // status is unknown, so the query deliberately stays frozen here rather
      // than risk an automatic refetch showing a false empty state for a file
      // that is still COMMITTING), a legacy rolled-back commit (response.data
      // .errors list), or a synchronous 4xx/503 from schedule_commit (FastAPI
      // HTTPException -> response.data.detail, string or object). None of
      // these are translated or written for an end user, `mc_commit/`'s own
      // error strings are developer prose (raw Python names, `!r`-quoted
      // values, internal terms like "leveled-BOM") in nearly every case, so
      // the UI never echoes them; one fixed, translated line covers all four.
      toast.error(translate("MC_MD_MAP_COMMIT_ERROR"))
    } finally {
      setIsCommitting(false)
    }
  }, [
    analyzedFiles,
    deletedFileIds,
    committedResults,
    columnOverrides,
    confirmMapping,
    queryClient,
    commitFile,
    isCommitting,
    soldAsIs,
    productGroupReimportMode,
    supplierNumberTwinsMode,
    supplierNumberTwins,
    translate,
  ])

  const [activeStepId, setActiveStepId] = useState<string>(REVIEW_STEP_ID)
  // Tracks files the user has explicitly advanced past via "Next file".
  // Only ever written in handleNext — NOT on passive navigation or initial load.
  // This ensures a file only shows as "complete" (yellow) after the user has
  // reviewed and clicked through it, regardless of AI pre-mapping.
  const [completedFileIds, setCompletedFileIds] = useState<string[]>([])
  const [activeSheetName, setActiveSheetName] = useState<string | null>(null)
  const [activeColumnRef, setActiveColumnRef] = useState<string | null>(null)

  const handleActiveColumnChange = useCallback(
    (sheetName: string | null, columnRef: string) => {
      setActiveSheetName(sheetName)
      setActiveColumnRef(columnRef)
    },
    [],
  )

  useEffect(() => {
    if (!fileSteps.length) {
      setActiveStepId(REVIEW_STEP_ID)
      return
    }
    const isKnownFileStep = fileSteps.some((s) => s.id === activeStepId)
    if (isKnownFileStep) return

    // On initial load (or after a file is deleted) we land at REVIEW_STEP_ID.
    // Jump to the first file automatically unless the user has already
    // completed every file and intentionally reached the review step.
    const allCompleted = fileSteps.every((s) => completedFileIds.includes(s.id))
    if (activeStepId === REVIEW_STEP_ID && !allCompleted) {
      setActiveStepId(fileSteps[0].id)
    }
  }, [activeStepId, fileSteps, completedFileIds])

  // `?step=` opens the flow at a named stage, which is what makes the agent's
  // hand-over (see `open_mc_import`) able to address a control that is component
  // state rather than a route. Applied once per distinct (flow, step) pair,
  // tracked by ref: re-applying on every render would yank the user back here
  // the moment they clicked a different step card. Deliberately NOT written
  // back on manual step changes — the URL is an entry point, not a mirror of
  // the current stage.
  //
  // Keyed on the flow's file_ids too, not the step string alone: two DIFFERENT
  // hand-overs can share the same step literal — "review" names every flow's
  // Review & Commit stage, no matter whose files it covers — so keying on the
  // step alone would see the second hand-over as a repeat of the first (same
  // string, ref already set) and silently skip it, leaving the user on the
  // first flow's screen. `fileIds` is the URL's own file_ids (read above),
  // which is what identifies which flow this is; sorted and joined so the
  // comparison doesn't care what order they arrived in.
  //
  // Deferred until the files have loaded so an id can be checked against them:
  // a `step` naming a file that was deleted (or belongs to another flow) is
  // ignored, and the effect above lands the user on the first file instead.
  const requestedStep = searchParams.get("step")
  const requestedFlowKey = [...fileIds].sort().join(",")
  const appliedStepRef = useRef<string | null>(null)
  useEffect(() => {
    if (!requestedStep || !fileSteps.length) return
    const key = `${requestedFlowKey}|${requestedStep}`
    if (appliedStepRef.current === key) return
    const resolved =
      requestedStep === IMPORT_REVIEW_STEP
        ? REVIEW_STEP_ID
        : fileSteps.find((s) => s.id === requestedStep)?.id
    if (!resolved) return
    appliedStepRef.current = key
    setActiveStepId(resolved)
  }, [requestedStep, requestedFlowKey, fileSteps])

  // Clear the group selection whenever the user navigates away from the
  // review step so it re-opens fresh next time.
  useEffect(() => {
    if (activeStepId !== REVIEW_STEP_ID) {
      setSelectedGroup("")
    }
  }, [activeStepId, setSelectedGroup])

  const activeFileStep =
    activeStepId === REVIEW_STEP_ID
      ? null
      : (fileSteps.find((s) => s.id === activeStepId) ?? null)

  // Resolve the currently highlighted sheet + column, falling back to the first.
  const activeSheetGroups = useMemo(
    () => (activeFileStep ? groupColumnsBySheet(activeFileStep.columns) : []),
    [activeFileStep],
  )
  const activeSelectionValid = activeFileStep?.columns.some(
    (column) =>
      column.columnRef === activeColumnRef &&
      column.sheetName === activeSheetName,
  )
  const resolvedActiveSheetName = activeSelectionValid
    ? activeSheetName
    : (activeSheetGroups[0]?.sheetName ?? null)
  const resolvedActiveColumnRef = activeSelectionValid
    ? activeColumnRef
    : (activeSheetGroups.find((g) => g.sheetName === resolvedActiveSheetName)
        ?.columns[0]?.columnRef ?? null)

  const effectiveAssignment = useCallback(
    (fileId: string, columnKey: string, aiAssigned: string | null) => {
      const override = columnOverrides[fileId]?.[columnKey]
      return override === undefined ? aiAssigned : override
    },
    [columnOverrides],
  )

  // A batch that maps its own product key already says what is sold. The
  // sell-through option is a fallback for uploads without product data, and
  // showing it here would let a distributor turn its whole purchase list into
  // products (TUR-1548). Overrides count: the user can map or unmap the key.
  const batchMapsProductNumber = useMemo(
    () =>
      fileSteps.some((step) =>
        step.columns.some(
          (col) =>
            effectiveAssignment(
              step.id,
              scopedColumnKey(col.sheetName, col.columnName),
              col.aiAssigned,
            ) === "products:product_number",
        ),
      ),
    [fileSteps, effectiveAssignment],
  )
  const showSoldAsIs = !batchMapsProductNumber

  // A value toggled on earlier must never survive the switch disappearing,
  // nor an agent hand-over to a different batch (`requestedFlowKey`) that
  // never unmounts this component — two articles-only batches in a row would
  // otherwise carry batch A's `true` into batch B's commit.
  useEffect(() => {
    setSoldAsIs(false)
  }, [showSoldAsIs, requestedFlowKey])

  // TUR-1539 §3.6: only offer the reimport choice when this batch actually
  // maps a column onto articles.product_group, mirroring the commit runner's
  // own gate (_mapping_maps_product_group) so the control never appears for
  // a file where the choice would be a no-op.
  const hasProductGroupColumn = useMemo(
    () =>
      fileSteps.some((step) =>
        step.columns.some(
          (col) =>
            effectiveAssignment(
              step.id,
              scopedColumnKey(col.sheetName, col.columnName),
              col.aiAssigned,
            ) === "articles:product_group",
        ),
      ),
    [fileSteps, effectiveAssignment],
  )

  useEffect(() => {
    setProductGroupReimportMode("keep_empty")
  }, [hasProductGroupColumn, requestedFlowKey])

  // A "create" picked for one batch must not carry over to the next one: this
  // component stays mounted when only the batch changes.
  useEffect(() => {
    setSupplierNumberTwinsMode("link")
  }, [requestedFlowKey])

  const fileStats = useMemo(() => {
    return Object.fromEntries(
      fileSteps.map((step) => {
        const { mapped, skipped, unmapped } = countAssignments(
          step.columns.map((col) =>
            effectiveAssignment(
              step.id,
              scopedColumnKey(col.sheetName, col.columnName),
              col.aiAssigned,
            ),
          ),
        )
        const allSkipped = skipped > 0 && skipped === step.columns.length
        return [
          step.id,
          {
            assigned: mapped,
            total: step.columns.length - skipped,
            // Same rule as FileMappingStep's Next: nothing left undecided, and
            // not every column skipped. The step cards are clickable, so a
            // file can reach Review without Next; this is what still gates it.
            ready: unmapped === 0 && !allSkipped,
          },
        ]
      }),
    )
  }, [effectiveAssignment, fileSteps])

  // Collect data quality warnings per file from the analyzed column mappings.
  // GROUP_COLUMN_AMBIGUOUS is excluded: it is a frozen analyze-time snapshot,
  // while FileMappingStep's own competingGroupColumns banner reads the LIVE
  // effective assignment (AI mapping + this file's overrides) — showing both
  // would either duplicate the same message or, once the user resolves the
  // ambiguity via an override, leave the stale server warning asserting it.
  const fileWarnings = useMemo<Record<string, DataQualityWarning[]>>(() => {
    const out: Record<string, DataQualityWarning[]> = {}
    for (const file of analyzedFiles) {
      if (deletedFileIds.includes(file.fileId)) continue
      out[file.fileId] = (file.columnMapping?.warnings ?? []).filter(
        (w) => w.code !== "GROUP_COLUMN_AMBIGUOUS",
      )
    }
    return out
  }, [analyzedFiles, deletedFileIds])

  const writableGroups = groups.filter(
    (g) => g.status !== "missing" && g.count > 0,
  )
  const totalToWrite = writableGroups.reduce((s, g) => s + g.count, 0)
  // A file counts as "done" only when the user has explicitly clicked "Next"
  // past it AND it is still ready (every column decided, at least one mapped).
  const allFilesReviewed =
    fileSteps.length === 0 ||
    fileSteps.every(
      (s) => completedFileIds.includes(s.id) && fileStats[s.id]?.ready,
    )
  const canCommit = allFilesReviewed && writableGroups.length > 0

  // What the agent is told about this screen. The flow's stages are component
  // state with no route of their own, so `import_active_step` is the only way it
  // can tell a user standing on a file's column mapping from one standing at
  // Review & Commit. `import_can_commit` is the second half of that and cannot
  // be derived from anywhere else: reaching the review stage does NOT enable
  // Commit — the step cards are clickable, so a user can be looking at it with
  // files still un-walked — and the backend has no way to know that, since
  // `completedFileIds` is per-session and never persisted. Without it a
  // hand-over would happily pulse a disabled button. `import_file_ids` is the
  // set this flow covers, deliberately the same key the upload screen
  // registers, distinguished by `mc_entity`.
  useRegisterAgentContext({
    route: MASTER_DATA_MAPPING_ROUTE,
    entities: {
      mc_entity: "import_mapping",
      import_file_ids: fileSteps.map((step) => step.id),
      import_active_step:
        activeStepId === REVIEW_STEP_ID ? IMPORT_REVIEW_STEP : activeStepId,
      import_can_commit: canCommit,
    },
  })

  // Only provide an activeGroup when selectedGroup is a valid group type.
  const activeGroup =
    selectedGroup && groups.some((g) => g.type === selectedGroup)
      ? (groups.find((g) => g.type === selectedGroup) ?? null)
      : null

  const stepCards = useMemo<MappingStepCard[]>(() => {
    const cards: MappingStepCard[] = fileSteps.map((step) => {
      const stats = fileStats[step.id]
      const isActive = activeStepId === step.id
      const isComplete = stats.ready && completedFileIds.includes(step.id)
      return {
        id: step.id,
        title: step.fileName,
        subtitle: translate("MC_MD_MAP_CARD_STEP_SUBTITLE", {
          assigned: stats.assigned,
          total: stats.total,
        }),
        status: (isActive
          ? "active"
          : isComplete
            ? "complete"
            : "pending") as MappingStepCard["status"],
        tone: "file" as const,
      }
    })

    cards.push({
      id: REVIEW_STEP_ID,
      title: translate("MC_MD_MAP_REVIEW_COMMIT"),
      subtitle: translate("MC_MD_MAP_REVIEW_CARD_FILE_COUNT", {
        count: fileSteps.length,
      }),
      status: (activeStepId === REVIEW_STEP_ID
        ? "active"
        : "pending") as MappingStepCard["status"],
      tone: "review" as const,
    })

    return cards
  }, [activeStepId, completedFileIds, fileStats, fileSteps, translate])

  const setAssignment = useCallback(
    (fileId: string, columnKey: string, assignment: string | null) => {
      setColumnOverrides((current) => ({
        ...current,
        [fileId]: {
          ...(current[fileId] ?? {}),
          [columnKey]: assignment,
        },
      }))
    },
    [setColumnOverrides],
  )

  const handleNext = () => {
    if (!activeFileStep) return
    // Mark the current file as explicitly completed before advancing.
    setCompletedFileIds((prev) =>
      prev.includes(activeFileStep.id) ? prev : [...prev, activeFileStep.id],
    )
    const currentIndex = fileSteps.findIndex((s) => s.id === activeFileStep.id)
    const nextStep = fileSteps[currentIndex + 1]
    setActiveStepId(nextStep?.id ?? REVIEW_STEP_ID)
  }

  if (isLoading && fileSteps.length === 0) {
    return <MasterDataMappingModuleSkeleton />
  }

  return (
    <>
      <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden bg-card">
        <div className="mt-4 flex items-start justify-between px-6 pb-2">
          <ModuleHeader
            title={translate("MC_MD_MAP_TITLE")}
            helpArticle={{
              categoryId: "master-data",
              articleId: "master-data.mapping",
            }}
            subtitle={translate("MC_MD_MAP_SUBTITLE")}
          />
          <div className="flex shrink-0 items-center gap-2">
            {/* <Button
              variant="outline"
              size="sm"
              onClick={() =>
                navigate("/material-compliance/master-data/import")
              }
              className="gap-1.5"
            >
              <FolderPlus className="h-3.5 w-3.5" />
              Add more files
            </Button> */}
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={reset}
                    aria-label={translate("MC_MD_MAP_CLOSE_ARIA")}
                  >
                    ×
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-[220px]">
                  {translate("MC_MD_MAP_CLOSE_TOOLTIP")}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
        {!commitSummary && (
          <div className="flex items-center gap-4 px-6 py-1">
            <MasterDataMappingStepCards
              stepCards={stepCards}
              onStepSelect={setActiveStepId}
              onDeleteFile={handleDeleteFile}
              deletingFileId={deletingFileId}
            />
          </div>
        )}

        {commitSummary && (
          <MasterDataCommitSummary
            summary={commitSummary}
            onDone={() =>
              // A rolled-back file needs the user back on this flow to retry
              // (see the "skip on retry" filter in handleCommit above) — only
              // a clean commit navigates away to the master-data overview.
              commitSummary.errors.length > 0
                ? setCommitSummary(null)
                : navigate("/material-compliance/master-data", {
                    replace: true,
                  })
            }
          />
        )}

        <div className="min-h-0 flex-1 overflow-hidden">
          {activeFileStep ? (
            <FileMappingStep
              fileStep={activeFileStep}
              warnings={fileWarnings[activeFileStep.id] ?? []}
              activeSheetName={resolvedActiveSheetName}
              activeColumnRef={resolvedActiveColumnRef}
              hasLocalChanges={
                Object.keys(columnOverrides[activeFileStep.id] ?? {}).length > 0
              }
              onActiveColumnChange={handleActiveColumnChange}
              onAssignmentChange={(columnKey, assignment) =>
                setAssignment(activeFileStep.id, columnKey, assignment)
              }
              onResetAssignment={(columnKey, assignment) =>
                setAssignment(activeFileStep.id, columnKey, assignment)
              }
              resolveAssignment={(columnKey, aiAssigned) =>
                effectiveAssignment(activeFileStep.id, columnKey, aiAssigned)
              }
              onDiscard={() => handleDiscardFileOverrides(activeFileStep.id)}
              onNext={handleNext}
              nextLabel={
                fileSteps[fileSteps.length - 1]?.id === activeFileStep.id
                  ? translate("MC_MD_MAP_REVIEW_COMMIT")
                  : translate("MC_MD_MAP_NEXT_FILE")
              }
            />
          ) : (
            <MasterDataReviewStep
              groups={groups}
              activeGroup={activeGroup}
              selectedGroupType={selectedGroup}
              onGroupSelect={setSelectedGroup}
              onBackToMapping={() =>
                setActiveStepId(fileSteps[0]?.id ?? REVIEW_STEP_ID)
              }
              onCommit={handleCommit}
              canCommit={canCommit}
              committing={isCommitting}
              totalToWrite={totalToWrite}
              fileCount={fileSteps.length}
              skippedComplianceColumns={skippedComplianceColumns}
              supplierNumberTwins={supplierNumberTwins}
              supplierNumberTwinsMode={supplierNumberTwinsMode}
              onSupplierNumberTwinsModeChange={setSupplierNumberTwinsMode}
              soldAsIs={soldAsIs}
              onSoldAsIsChange={showSoldAsIs ? setSoldAsIs : undefined}
              productGroupReimportMode={productGroupReimportMode}
              productGroupLabel={productGroupLabel}
              onProductGroupReimportModeChange={
                hasProductGroupColumn ? setProductGroupReimportMode : undefined
              }
            />
          )}
        </div>
      </div>
    </>
  )
}
